import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Link,
  Paper,
  Rating,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon,
  PersonOutline as PersonIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import ProviderReviews from '../../components/directory/ProviderReviews';
import BookingPanel from '../../components/directory/BookingPanel';
import OffersPanel from '../../components/directory/OffersPanel';
import OpeningHoursTable from '../../components/directory/OpeningHoursTable';
import ClaimPanel from '../../components/directory/ClaimPanel';
import { apiCall } from '../../utils/fetchAPI';
import { Loading, PanelSkeleton } from '../../components/layout/Skeletons';
import { categoryLook } from '../../constants/directory';
import { translateApiMessage } from '../../utils/apiMessages';
import { useToast } from '../../context/ToastContext';

/** Puts a contact detail on the clipboard, and says so: the icon alone shows nothing. */
function CopyButton({ value, label }) {
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copié`);
    } catch {
      toast.error('Copie impossible');
    }
  };

  return (
    <Tooltip title={`Copier ${label.toLowerCase()}`}>
      <IconButton size="small" onClick={copy} aria-label={`Copier ${label.toLowerCase()}`} sx={{ ml: 0.5 }}>
        <CopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.75,
        alignItems: 'flex-start',
        py: 1.75,
        borderBottom: '1px solid',
        borderColor: 'surface.rule',
        '&:last-of-type': { borderBottom: 0, pb: 0 },
      }}
    >
      <Box sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0, display: 'flex' }}>{icon}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
        <Typography variant="caption">{label}</Typography>
        <Typography variant="body1" component="div" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

/** A section of the listing, so every block is framed the same way. */
function Panel({ title, children, ...rest }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }} {...rest}>
      {title && (
        <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

function ProviderPage() {
  const { slug } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProvider = async () => {
      setLoading(true);

      try {
        const response = await apiCall(`/providers/${slug}`, { method: 'GET' });

        if (cancelled) return;

        if (404 === response.status) {
          setNotFound(true);
          return;
        }

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(translateApiMessage(data.error) || 'Cette fiche n’a pas pu être chargée.');
        }

        setProvider(data.data);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProvider();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement de la fiche">
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width={280} height={22} sx={{ mb: 2.5 }} />
            <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
              <Skeleton variant="rectangular" height={132} sx={{ borderRadius: 0 }} />
              <Box sx={{ p: { xs: 2.5, md: 3.5 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, justifyContent: 'space-between' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="45%" height={46} />
                  <Skeleton variant="rectangular" width={200} height={32} sx={{ borderRadius: 999, mt: 1.5 }} />
                  <Skeleton variant="text" width={220} height={26} sx={{ mt: 1.5 }} />
                </Box>
                <Stack sx={{ gap: 1.25, width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 999 }} />
                  <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 999 }} />
                </Stack>
              </Box>
            </Paper>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.85fr 1fr' }, gap: 3, alignItems: 'start' }}>
              <Stack spacing={3}>
                <PanelSkeleton lines={4} />
                <PanelSkeleton height={220} />
              </Stack>
              <PanelSkeleton lines={5} />
            </Box>
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  if (notFound) {
    return (
      <PageLayout>
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', maxWidth: 520 }}>
          <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
            Cette fiche n'existe pas
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Elle a peut-être été retirée de l'annuaire.
          </Typography>
          <Button component={RouterLink} to="/services" variant="contained">
            Retour à l'annuaire
          </Button>
        </Paper>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert severity="error">{error}</Alert>
      </PageLayout>
    );
  }

  const { contact, address, rating } = provider;
  const hasAddress = Boolean(address.street && address.postal_code);
  // A private individual's street is not published — it is usually their
  // home — so the page says where they work instead.
  const hasArea = !hasAddress && Boolean(address.postal_code);
  // Contact details are optional, so the card can have nothing to show.
  const hasAnyContact = Boolean(
    contact.phone_formatted || contact.email || contact.website || hasAddress || hasArea
  );
  const look = categoryLook(provider.category);
  // A shop's category lives in the shops' tree, so the trail has to lead back
  // to the page that browses it — the services page would filter on a
  // category it does not list and answer "rien pour l'instant".
  const isShop = 'COMMERCE' === provider.kind;
  const directory = { path: isShop ? '/commerces' : '/services', label: isShop ? 'Les commerces' : 'Les services' };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <Stack
          component="nav"
          aria-label="Fil d'Ariane"
          direction="row"
          sx={{ alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}
        >
          <Link component={RouterLink} to={directory.path} variant="body2">
            {directory.label}
          </Link>
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Link
            component={RouterLink}
            to={`${directory.path}?category=${provider.category.slug}`}
            variant="body2"
          >
            {provider.category.name}
          </Link>
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {provider.name}
          </Typography>
        </Stack>

        {/* The two things a visitor actually came to do sit at the top, level
            with the name, rather than under a fold of prose. */}
        <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
          <Box
            aria-hidden="true"
            sx={{
              height: 132,
              bgcolor: look.tint,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(look.icon, { sx: { fontSize: 58, color: look.ink } })}
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ p: { xs: 2.5, md: 3.5 }, gap: 3, justifyContent: 'space-between', alignItems: { md: 'flex-start' } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '2rem', md: '2.375rem' } }}>
                {provider.name}
              </Typography>

              <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={provider.category.name}
                  component={RouterLink}
                  to={`${directory.path}?category=${provider.category.slug}`}
                  clickable
                  sx={{ bgcolor: 'warning.light', color: 'primary.dark', fontWeight: 700 }}
                />
                {provider.neighborhood && (
                  <Chip icon={<PlaceIcon />} label={provider.neighborhood.name} />
                )}

                {/* Somebody offering a service as a private individual — a
                    student paid through CESU, say — has no SIRET and is not
                    hiding one. Saying so is what stops a blank meaning two
                    different things. */}
                {'INDIVIDUAL' === provider.legal_status && (
                  <Chip icon={<PersonIcon />} label="Particulier" variant="outlined" />
                )}
              </Stack>

              {/* No reviews is not a bad rating. */}
              {rating && rating.count > 0 ? (
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
                  <Rating value={rating.average} precision={0.1} readOnly />
                  <Typography variant="body1">
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {rating.average.toFixed(1).replace('.', ',')} sur 5
                    </Box>
                    {` · ${rating.count} avis de voisins`}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2">Aucun avis pour l'instant</Typography>
              )}
            </Box>

            <Stack sx={{ gap: 1.25, width: { xs: '100%', md: 300 }, flexShrink: 0 }}>
              {contact.phone_formatted && (
                <Button
                  component="a"
                  href={`tel:${contact.phone}`}
                  variant="contained"
                  size="large"
                  startIcon={<PhoneIcon />}
                  fullWidth
                >
                  {contact.phone_formatted}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.85fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Stack spacing={3}>
            {provider.description && (
              <Panel title="Présentation">
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {provider.description}
                </Typography>
              </Panel>
            )}

            {/* A shop is open or it is not; there is nothing to request, and
                an appointment form on a bakery only gets ignored. */}
            {provider.accepts_bookings ? (
              <BookingPanel provider={provider} />
            ) : (
              <OffersPanel provider={provider} />
            )}

            <Panel>
              <ProviderReviews
                providerSlug={provider.slug}
                citySlug={provider.city?.slug}
                isOwnListing={Boolean(provider.is_own_listing)}
                summary={provider.rating}
              />
            </Panel>
          </Stack>

          <Stack spacing={3}>
            <Panel title="Contact & infos">
              {!hasAnyContact && (
                <Typography variant="body2" color="text.secondary">
                  Aucune information de contact n’a été renseignée.
                </Typography>
              )}

              {contact.phone_formatted && (
                <ContactRow icon={<PhoneIcon fontSize="small" />} label="Téléphone">
                  <Link href={`tel:${contact.phone}`}>{contact.phone_formatted}</Link>
                  <CopyButton value={contact.phone_formatted} label="Le numéro" />
                </ContactRow>
              )}

              {contact.email && (
                <ContactRow icon={<EmailIcon fontSize="small" />} label="Courriel">
                  <Link href={`mailto:${contact.email}`}>{contact.email}</Link>
                  <CopyButton value={contact.email} label="L’email" />
                </ContactRow>
              )}

              {contact.website && (
                <ContactRow icon={<LanguageIcon fontSize="small" />} label="Site internet">
                  <Link href={contact.website} target="_blank" rel="noopener noreferrer">
                    {contact.website.replace(/^https?:\/\//, '')}
                  </Link>
                </ContactRow>
              )}

              {hasAddress && (
                <ContactRow icon={<PlaceIcon fontSize="small" />} label="Adresse">
                  {`${address.street}, ${address.postal_code} ${provider.city.name}`}
                </ContactRow>
              )}

              {hasArea && (
                <ContactRow icon={<PlaceIcon fontSize="small" />} label="Secteur">
                  {`${address.postal_code} ${provider.city.name}`}
                </ContactRow>
              )}

              {/* Only a shopfront has opening hours, so only a shop is asked for them. */}
              {(provider.opening_hours || 'COMMERCE' === provider.kind) && (
                <ContactRow icon={<ScheduleIcon fontSize="small" />} label="Horaires">
                  {provider.opening_hours?.length > 0 ? (
                    <OpeningHoursTable hours={provider.opening_hours} />
                  ) : (
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      Non renseignés
                    </Box>
                  )}
                </ContactRow>
              )}
            </Panel>

            {/* Only on a listing nobody maintains: the business it describes
                asks for it here rather than writing a second one. */}
            <ClaimPanel provider={provider} />
          </Stack>
        </Box>
      </Box>
    </PageLayout>
  );
}

export default ProviderPage;
